import { qs } from '@brands/ui/utils'
import axios from 'axios'

import { app } from '@/states/app-state'
import { stepUpState } from '@/states/step-up-state'
import { isStepUpRequiredError } from '@/utils/error'
import { getAbsoluteURL } from '@/utils/url'
import { apiService } from '~/core'

export * as api from './generated'

declare module 'axios' {
  interface AxiosRequestConfig {
    // For callers that render their own inline loader (e.g. a data grid) or fetch in the background.
    skipGlobalLoading?: boolean
    // For background fetches the user never asked for - failing silently is correct there.
    skipErrorToast?: boolean
    // Set on the single replay after a step-up verification, so a second 403 is not retried again.
    stepUpRetried?: boolean
  }
}

axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params)
}

axios.interceptors.request.use(
  (request) => {
    if (!request.skipGlobalLoading) app.showLoading()

    request.url = getAbsoluteURL(request.url)

    for (const [key, value] of Object.entries(apiService.getAxiosConfig())) {
      request.headers.set(key, value as string)
    }

    return request
  },
  (error) => {
    app.hideLoading()
    return Promise.reject(error as Error)
  },
)

axios.interceptors.response.use(
  (response) => {
    if (!response.config.skipGlobalLoading) app.hideLoading()
    return response
  },
  async (error) => {
    if (!axios.isAxiosError(error) || !error.config?.skipGlobalLoading) app.hideLoading()

    // A protected write needs a fresh second factor: ask through the dialog, then replay the request once.
    // A request fired from inside the open dialog gets no dialog of its own - it falls through and reports the error.
    if (axios.isAxiosError(error) && error.config && !error.config.stepUpRetried && isStepUpRequiredError(error)) {
      const verification = stepUpState.request()
      if (verification) {
        const verified = await verification
        if (verified) return axios.request({ ...error.config, stepUpRetried: true })

        error.message = 'Verification cancelled'
        return Promise.reject(error as Error)
      }
    }

    if (!axios.isCancel(error) && !(axios.isAxiosError(error) && error.config?.skipErrorToast)) {
      app.showErrorToast(error)
    }

    return Promise.reject(error as Error)
  },
)
